// Libraries import
import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { H2, H4 } from '../../../ComponentLibrary/text-elements/oc-h';
import FormButton from "../../../ComponentLibrary/input-elements/oc-form-button";
import FormModal from "../../../ComponentLibrary/forms/oc-form-modal";

// Assets import
import IconInfo from "../../../ComponentLibrary/icons/icon-info";
import IconPdf from "../../../ComponentLibrary/icons/icon-pdf";

// Components import
import { QuotingWidgetPdf } from "../QuotingWidgetPdf";
import LoadingEstimation from "./LoadingEstimation";
import EstimationNotFound from "./EstimationNotFound";
import ContactMethods from "./ContactMethods";

// Modals import
import ContingencyModalContent from "../modals/ContingencyModalContent";

// Utilities import
import { formatMoney, totalContingenciesPrice, presentMoneyRange } from "../functions/sharedFunctions";

// Styled Components
const EstimationForHomeOwnerContainer = styled.div`
  padding: 40px;
  width: 100%;

  @media screen and (max-width: 767px) {
    padding: 10px;
  }
`;

const EstimateSection = styled.section`
  border-bottom: 1px solid #000000;
  display: inline-block;
  position: relative;
  margin-bottom: 32px;
  margin-top: 0;
  max-width: none;
  padding-bottom: 16px;
  width: 100%;

  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
  }

  @media screen and (max-width: 767px) {
    p {
      font-size: 14px;
    }
  }
`;

const Message = styled.div`
  margin-bottom: 16px;

  h5 {
    font-size: 20px;
    line-height: 21px;
    margin-bottom: 8px;
  }

  p {
    line-height: 16px;
    margin-top: 8px;
  }
`;

const FlexRowContainer = styled.div`
  display: flex;
  gap: 18px;

  @media screen and (max-width: 767px) {
    flex-wrap: wrap;
  }

  a {
    img {
      height: 175px;
      object-fit: cover;
      width: 175px;
    }
  }
`;

const StandOutHeading = styled(H4)`
  margin-bottom: 16px;
`;

const StandOutsContainer = styled.div`
  column-gap: 16px;
  display: flex;
  flex-wrap: wrap;
`;

const StandOut = styled.div`
  display: inline-block;
  position: relative;
  width: calc(50% - 8px);

  @media screen and (max-width: 767px) {
    width: 100%;
  }

  h4 {
    margin-bottom: 8px;
  }

  p {
    margin-top: 0;
  }
`;

const QuoteDetailsContainer = styled.div`
  background-color: white;
  display: inline-block;
  padding: 32px;
  position: relative;
  width: 100%;

  @media screen and (max-width: 767px) {
    padding: 16px 10px;
  }
`;

const DetailHeader = styled.div`
  border-bottom: 2px solid #000000;
  border-top: 2px solid #000000;
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  padding: 21px 24px;
  position: relative;

  @media screen and (max-width: 767px) {
    flex-wrap: wrap;
  }

  h4 {
    margin-bottom: 0;
    margin-right: 16px;
    padding-top: 4px;

    @media screen and (max-width: 767px) {
      margin-bottom: 16px;
    }
  }

  button {
    flex-shrink: 0;
  }
`;

const DetailSection = styled.div`
  border-bottom: 2px solid #000000;
  display: inline-block;
  padding-bottom: 32px;
  padding-top: 32px;
  position: relative;
  width: 100%;

  &.table-container {
    border-bottom: none;
    padding-bottom: 0;
    padding-top: 0;
  }

  &:last-of-type {
    border-bottom: 0;
  }

  h4 {
    margin-bottom: 24px;
  }
`;

const HouseDetails = styled.div`
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: 767px) {
    flex-wrap: wrap;
  }
`;

const DetailContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  position: relative;
  width: calc(100% / 3);

  @media screen and (max-width: 767px) {
    margin-bottom: 16px;
    width: 100%;
  }

  strong, span {
    margin-bottom: 8px;
  }

  span {
    font-size: 14px;
    line-height: 18px;
  }
`;

const PseudoTable = styled.div`
  border-bottom: 2px solid #000000;
  display: inline-block;
  margin-bottom: 64px;
  position: relative;
  width: 100%;

  @media screen and (max-width: 767px) {
    margin-bottom: 32px;
  }

  &.border-top {
    border-top: 2px solid #000000;
  }

  &.last {
    margin-bottom: 0;
  }

  > div:last-of-type {
    border-bottom: none;
  }
`;

const TableRow = styled.div`
  align-items: center;
  border-bottom: 1px solid #6D6D6D;
  display: flex;
  justify-content: space-between;
  padding: 16px 25px;

  @media screen and (max-width: 767px) {
    padding-left: 10px;
    padding-right: 10px;
  }
`;

const TableHeader = styled(TableRow)`
  border-bottom: 2px solid #000000;

  > div {
    display: inline-block;

    h4 {
      margin-bottom: 0;
    }
  }

  h4 {
    font-family: OCRoboto;
    font-size: 24px;
    font-weight: 700;
    line-height: 26px;
    text-transform: none;
  }

  h4, h5 {
    color: #D40F7D;
    margin-bottom: 0;
    width: 100%;

    @media screen and (max-width: 767px) {
      font-size: 14px;
      line-height: 18px;
    }
  }

  span {
    font-size: 12px;
    line-height: 17px;
  }
`;

const TableKey = styled(TableRow)``;

const TableCell = styled.div`
  align-items: center;
  display: inline-flex;
  flex-wrap: wrap;

  @media screen and (max-width: 767px) {
    font-size: 14px;
    line-height: 18px;
  }
`;

const HomeownerInfoPanel = styled.div`
  column-gap: 24px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  position: relative;
`;

const HomeownerInfoContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  position: relative;
  width: calc(50% - 12px);

  @media screen and (max-width: 767px) {
    width: 100%;
  }
`;

const HomeownerInfo = styled.div`
  display: inline-flex;
  flex-direction: column;
  margin-bottom: 16px;
  position: relative;
  width: 100%;

  &.photo-container {
    flex-direction: row;
    flex-wrap: wrap;

    h6 {
      width: 100%;
    }
  }

  h6, span {
    margin-bottom: 8px;
  }

  span {
    line-height: 17px;
  }
`;

const PhotoContainer = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: scroll;

  a {
    display: inline-block;
    flex-shrink: 0;
    position: relative;

    img {
      height: 200px;
      object-fit: cover;
      width: initial;
    }
  }
`;

const Thumbnail = styled.img`
  display: inline-block;
  height: 106px;
  position: relative;
  width: 106px;
`;

// Container for click event binding, can't bind directly to icon SVG
const IconContainer = styled.span`
  display: inline-flex;
  position: relative;
`;

const Icon = styled(IconInfo)`
  cursor: pointer;
`;

const PdfIcon = styled(IconPdf)`
  position: relative;
  right: 0.375rem;
`

const EstimationForHomeOwner = ({ estimateId, configuration }) => {
  const [loading, setLoading] = useState(true);
  const [modalContent, setModalContent] = useState(null);
  const [quoteEstimation, setQuoteEstimation] = useState(null);
  const [homeownerName, setHomeownerName] = useState('');
  const [contactInfo, setContactInfo] = useState(null);
  const [roofAdditionalInfo, setRoofAdditionalInfo] = useState(null);
  const [roofDetails, setRoofDetails] = useState(null);
  const [uploads, setUploads] = useState(null);
  const [shingleInfo, setShingleInfo] = useState({});
  const contractorName = configuration.companyName;
  const contractorPhoto = configuration.contractorPhoto;

  const standOuts = configuration.standOuts;
  const contractorPhone = configuration.companyPhone;
  const contractorEmail = configuration.communicationsEmail || configuration.leadsEmail;
  const [downloadPdf, setDownloadPdf] = useState(false)

  const totalEstimationWithoutContingencies = () => {
    const additionalStructuresMinTotal = Object.keys(quoteEstimation.additionalStructures).reduce(
      (totalPrice, structure) => totalPrice + quoteEstimation.additionalStructures[structure].minEstimate, 0
    ) || 0;
    const additionalStructuresMaxTotal = Object.keys(quoteEstimation.additionalStructures).reduce(
      (totalPrice, structure) => totalPrice + quoteEstimation.additionalStructures[structure].maxEstimate, 0
    ) || 0;

    return [
      quoteEstimation.minEstimate + additionalStructuresMinTotal,
      quoteEstimation.maxEstimate + additionalStructuresMaxTotal
    ]
  };

  const totalEstimation = () => {
    const totalPrice = totalEstimationWithoutContingencies()
    return [
      totalPrice[0] + totalContingenciesPrice(quoteEstimation.contingencies),
      totalPrice[1] + totalContingenciesPrice(quoteEstimation.contingencies)
    ]
  };

  const triggerPdfDownload = () => {
    setDownloadPdf(true);
  };

  useEffect(() => {
    if (loading) {
      fetch(`/roofing/quoting-widget/${configuration.id}/homeowner-estimation/${estimateId}`).then(
        (response) => response.json()
      ).then(
        (responseJson) => {
          const roofDetails = responseJson.roofDetails;
          const additionalStructures = roofDetails.find(details => details.inputField === 'additional-structures-with-size').value;
          const estimation = {
            minEstimate: responseJson.estimation.min_estimate,
            maxEstimate: responseJson.estimation.max_estimate,
            contingencies: responseJson.estimation.contingencies,
            additionalStructures: {},
            custom_message: responseJson.estimation.custom_message
          };
          Object.keys(additionalStructures).forEach(structure => (
            estimation.additionalStructures[structure] = {
              minEstimate: responseJson.estimation[`${additionalStructures[structure].formInputField}_min_estimate`],
              maxEstimate: responseJson.estimation[`${additionalStructures[structure].formInputField}_max_estimate`],
              label: additionalStructures[structure].label,
            }
          ));
          setQuoteEstimation(estimation);
          setHomeownerName(responseJson.homeownerName);
          setContactInfo(responseJson.contactInfo);
          setRoofAdditionalInfo(responseJson.roofAdditionalInfo);
          setRoofDetails(roofDetails);
          setUploads(responseJson.uploads);
          setLoading(false);
        }

      ).catch(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    if (roofAdditionalInfo) {
      setShingleInfo(roofAdditionalInfo.find(info => info.inputField === 'shingle-color'));
    }
  }, [roofAdditionalInfo]);

  const additionalStructures = roofDetails?.find(details => details.inputField === 'additional-structures-with-size')?.value;

  if (downloadPdf) {
    return (
      <QuotingWidgetPdf
        contractorName={contractorName}
        contractorPhone={contractorPhone}
        contractorEmail={contractorEmail}
        contactInfo={contactInfo}
        roofDetails={roofDetails}
        shingleInfo={shingleInfo}
        quoteEstimation={quoteEstimation}
        setDownloadPdf={setDownloadPdf}
        totalEstimationWithoutContingencies={totalEstimationWithoutContingencies()}
        totalEstimationWithContingencies={totalEstimation}
      />
    )
  }

  if (loading) {
    return <LoadingEstimation />;
  }


  return (
    <EstimationForHomeOwnerContainer>
      {!quoteEstimation &&
        <EstimationNotFound
          contractorName={contractorName}
          contractorPhone={contractorPhone}
          contractorEmail={contractorEmail}
          standOuts={standOuts}
        />
      }

      {quoteEstimation &&
        <>
          <FlexRowContainer>
            {contractorPhoto &&
              <a
                data-track="file-download"
                data-track-doc-type="Image"
                data-track-file-name="quoting-widget-image-attachment"
                href={contractorPhoto}
                target="_blank"
              >
                <img src={contractorPhoto}/>
              </a>
            }
            <EstimateSection>
              <H2>You have a new quote estimation!</H2>
              <p>Based on the information you provided, we have calculated a quote on a new roof for your home. Please review and let us know if you have questions.</p>

              <ContactMethods
                contractorPhone={contractorPhone}
                contractorEmail={contractorEmail}
              />
            </EstimateSection>
          </FlexRowContainer>

          {standOuts &&
            <EstimateSection>
              <StandOutHeading font="body">About {contractorName}</StandOutHeading>
              <StandOutsContainer>
                {standOuts.filter((standOut) => standOut.display).map((standOut, index) => (
                  <StandOut key={`stand-out-${index}`}>
                    <h6>{standOut.title}</h6>
                    <p>{standOut.content}</p>
                  </StandOut>
                ))}
              </StandOutsContainer>
            </EstimateSection>
          }

          <QuoteDetailsContainer>
            {quoteEstimation.custom_message &&
              <Message>
                <h5>Personal message from {contractorName}</h5>
                <p>{quoteEstimation.custom_message}</p>
              </Message>
            }

            <DetailHeader>
              <H4 font="body">Roofing Quote Estimation from {contractorName}</H4>
              <FormButton onClick={() => triggerPdfDownload()}>
                <PdfIcon /> <span>Download Quote</span>
              </FormButton>
            </DetailHeader>

            <DetailSection>
              <HouseDetails>
                <DetailContainer>
                  <h6>Location:</h6>
                  <span dangerouslySetInnerHTML={{ __html: contactInfo?.find((data) => data.inputField == 'address').value }}></span>
                </DetailContainer>
                <DetailContainer>
                  <h6>Structures:</h6>
                  <span><strong>Main House</strong></span>
                  <span>{roofDetails.find(details => details.inputField === 'area').value} sq. ft.</span>

                  {Object.keys(additionalStructures).map((structure) => (
                    <>
                      <span><strong>{additionalStructures[structure].label}</strong></span>
                      <span>{additionalStructures[structure].value} sq.ft</span>
                    </>
                  ))}
                </DetailContainer>
                <DetailContainer>
                  <h6>Shingle Selection:</h6>
                  <span><strong>Type</strong></span>
                  <span>Owens Corning {shingleInfo.shingleLine}</span>

                  <span><strong>Color</strong></span>
                  <span>
                    <a href={shingleInfo.shingleLink} target="_blank">
                      {shingleInfo.shingleColor} (View Color)
                    </a>
                  </span>
                </DetailContainer>
              </HouseDetails>
            </DetailSection>

            <DetailSection className="table-container">
              <PseudoTable>
                <TableHeader>
                  <TableCell>
                    <h4>Quote Estimation</h4>
                    {quoteEstimation.contingencies?.length &&
                      <span>Contingencies not included</span>
                    }
                  </TableCell>
                  <TableCell>
                    <h4>{presentMoneyRange(totalEstimationWithoutContingencies()[0], totalEstimationWithoutContingencies()[1])}</h4>
                  </TableCell>
                </TableHeader>
                <TableKey>
                  <TableCell>
                    <strong>Cost Breakdown</strong>
                  </TableCell>
                </TableKey>
                <TableRow>
                  <TableCell>Main House</TableCell>
                  <TableCell>{presentMoneyRange(quoteEstimation.minEstimate, quoteEstimation.maxEstimate)}</TableCell>
                </TableRow>
                {Object.keys(additionalStructures).map((structure) => (
                  <TableRow key={structure}>
                    <TableCell>{additionalStructures[structure]["label"]}</TableCell>
                    <TableCell>
                      {presentMoneyRange(
                        quoteEstimation.additionalStructures[structure].minEstimate,
                        quoteEstimation.additionalStructures[structure].maxEstimate
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </PseudoTable>

              {quoteEstimation.contingencies?.length &&
                <PseudoTable className="border-top last">
                  <TableHeader>
                    <TableCell>
                      <h5>Quote Estimation</h5>
                      <span>All Contingencies Included</span>
                    </TableCell>
                    <TableCell>
                      <h5>{presentMoneyRange(totalEstimation()[0], totalEstimation()[1])}</h5>
                    </TableCell>
                  </TableHeader>
                  <TableKey>
                    <TableCell>
                      <strong>Contingencies</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Possible Additional Costs</strong>
                    </TableCell>
                  </TableKey>
                  {quoteEstimation.contingencies.map((contingency) => (
                    <TableRow key={contingency.name}>
                      <TableCell>
                        {contingency.name}&nbsp;
                        <IconContainer
                          data-track="tooltip"
                          data-track-destination={contingency.name}
                          onClick={() => setModalContent(ContingencyModalContent(contingency))}
                        >
                          <Icon />
                        </IconContainer>
                      </TableCell>
                      <TableCell>{formatMoney(contingency.cost)}</TableCell>
                    </TableRow>
                  ))}
                </PseudoTable>}
            </DetailSection>

            <DetailSection>
              <H4 font="body">Next Steps</H4>
              <p>Now that you have your quote estimation, it's time to schedule a consult and start the process of getting your new roof! Contact {contractorName} today to get started!</p>

              <ContactMethods
                contractorPhone={contractorPhone}
                contractorEmail={contractorEmail}
              />
            </DetailSection>

            <DetailSection>
              <H4 font="body">Information Provided by the Homeowner</H4>

              <HomeownerInfoPanel>
                <HomeownerInfoContainer>
                  {contactInfo.map((contactObj, index) => (
                    <HomeownerInfo key={index}>
                      <h6>{contactObj.label}</h6>
                      <span dangerouslySetInnerHTML={{ __html: contactObj.value }}></span>
                    </HomeownerInfo>
                  ))}
                </HomeownerInfoContainer>

                <HomeownerInfoContainer>
                  <HomeownerInfo className="photo-container">
                    <h6>Photo Attachments</h6>
                    {uploads?.length > 0 &&
                      <PhotoContainer>
                        {uploads.map((upload, index) => (
                          <a
                            key={`image-link-${index}`}
                            data-track="file-download"
                            data-track-doc-type="Image"
                            data-track-file-name="quoting-widget-image-attachment"
                            href={upload}
                            target="_blank"
                          >
                            <img key={`image-${index}`} src={upload} />
                          </a>
                        ))}
                      </PhotoContainer>
                    }
                    {!uploads?.length > 0 &&
                      <span>No photos added</span>
                    }
                  </HomeownerInfo>
                </HomeownerInfoContainer>

                <HomeownerInfoContainer>
                  {roofDetails.map((detail, index) => {
                    if (detail.hidden) return;

                    return (
                      <HomeownerInfo key={`details-${index}`}>
                        <h6>{detail.label}</h6>
                        {Array.isArray(detail.value) ?
                          detail.value.map(value => (<span>{value}</span>))
                          :
                          <span>{detail.value}</span>
                        }
                        {detail.thumbnail && <Thumbnail src={detail.thumbnail} />}
                      </HomeownerInfo>
                    );
                  })}
                </HomeownerInfoContainer>

                <HomeownerInfoContainer>
                  {roofAdditionalInfo.map((detail, index) => (
                    <HomeownerInfo key={`additional-info-${index}`}>
                      <h6>{detail.label}</h6>
                      {Array.isArray(detail.value) ?
                        detail.value.map(value => (<span>{value}</span>))
                        :
                        <span>{detail.value}</span>
                      }
                      {detail.thumbnail && <Thumbnail src={detail.thumbnail} />}
                    </HomeownerInfo>
                  ))}
                </HomeownerInfoContainer>
              </HomeownerInfoPanel>
            </DetailSection>
          </QuoteDetailsContainer>
        </>
      }

      {modalContent &&
        <FormModal
          name="Quote for home owner Modal"
          onClose={() => setModalContent(null)}
          open={true}
        >
          {modalContent}
        </FormModal>
      }
    </EstimationForHomeOwnerContainer>
  );
};

export default EstimationForHomeOwner;
